import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import {
  getFaceUserProfile,
  persistFaceProfileSnapshot,
} from '../../face-identity/utils/faceIdentityApi';
import CountryWhatsAppInput from '../../../shared/components/CountryWhatsAppInput';
import { DEFAULT_WHATSAPP_COUNTRY } from '../../../shared/utils/whatsappPhone';
import {
  isRegisterMobileComplete,
  mobileValidationError,
  normalizeRegisterMobileValue,
} from '../../shared/mobileDigits';
import {
  checkinFromEngagementBooking,
  createPartnerCheckinQueue,
  fetchActiveVisit,
  lookupStylistCustomerByMobile,
} from '../../salon-admin/utils/checkinQueueApi';
import GuestWelcomePassportPanel from '../../face-identity/components/GuestWelcomePassportPanel';
import WelcomePreferencesCard from '../../face-identity/components/WelcomePreferencesCard';
import { useKioskRegisterForm } from '../../face-identity/hooks/useKioskRegisterForm';
import { SALON_APP_BASE, SALON_SERVICE_LOBBY_PATH, SALON_STYLIST_GUEST_PATH } from '../config/salonApp';
import { stylistGuestGenderReady } from '../utils/stylistGuestProfile';
import { handoffFromCheckin } from '../utils/visitHandoff';
import { setActiveCheckinSession } from '../utils/salonFaceSession';
import { KIOSK_MODE_DESK, setSalonKioskMode } from '../utils/salonKioskMode';
import { readFranchiseFlowAuth } from '../../shared/franchiseFlowAuth';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import '../../face-identity/pages/FaceIdentityWelcomePage.css';
import './SalonCustomerCodePage.css';
import './VisitIntentPage.css';
import ExpectedTodayBookings from '../components/ExpectedTodayBookings';
import '../../face-identity/components/WelcomePreferencesCard.css';

function maskMobile(mobile) {
  const d = String(mobile || '').replace(/\D/g, '');
  if (d.length < 6) return mobile || '';
  return `•••• ${d.slice(-4)}`;
}

function snapshotFromLookupUser(user, mobile) {
  const profilePayload = user?.profile || null;
  const snap = {
    faceUserId: user?.id,
    user,
    profile: profilePayload,
    fromMobileLookup: true,
    mode: 'stylist_led',
  };
  persistFaceProfileSnapshot(snap);
  return { user, mobile, profile: profilePayload };
}

async function loadProfileForUser(user, mobile) {
  const id = String(user?.id || '').trim();
  let fullUser = user;
  if (id) {
    try {
      const prof = await getFaceUserProfile(id);
      if (prof?.user) {
        fullUser = {
          ...prof.user,
          profile: prof.profile || prof.user.profile,
        };
      }
    } catch {
      /* use lookup row */
    }
  }
  const snap = {
    faceUserId: fullUser.id,
    user: fullUser,
    profile: fullUser.profile || null,
    fromMobileLookup: true,
    mode: 'stylist_led',
  };
  const profilePayload = fullUser.profile || null;
  snap.profile = profilePayload;
  persistFaceProfileSnapshot(snap);
  return { user: fullUser, mobile, profile: profilePayload };
}

/** Same guest when the full names match, or the first names do ("Rishi" vs "rishi kumar"). */
function sameGuestName(a, b) {
  const norm = (v) => String(v || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const x = norm(a);
  const y = norm(b);
  if (!x || !y || x === 'guest' || y === 'guest') return true;
  return x === y || x.split(' ')[0] === y.split(' ')[0];
}

export default function StylistCustomerFlowPage() {
  useFaceIdentityScrollShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const { registerForm } = useKioskRegisterForm();
  readFranchiseFlowAuth();

  const [step, setStep] = useState('lookup');
  const [engagementBooking, setEngagementBooking] = useState(null);
  const bookingLookupStartedRef = useRef(false);
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [activeVisit, setActiveVisit] = useState(null);
  const [notice, setNotice] = useState('');

  const displayName =
    String(profile?.user?.name || profile?.user?.full_name || 'Guest').trim() || 'Guest';

  const genderReady = profile?.user
    ? stylistGuestGenderReady(profile.user, profile.profile)
    : false;

  const openProfile = useCallback((user, phone) => {
    const bundle = snapshotFromLookupUser(user, phone);
    setProfile(bundle);
    setCandidates([]);
    setStep('profile');
    setError('');
    void (async () => {
      try {
        const enriched = await loadProfileForUser(user, phone);
        setProfile(enriched);
        const open = await fetchActiveVisit({
          faceUserId: enriched.user.id,
          timeoutMs: 15000,
        });
        setActiveVisit(open?.id ? open : null);
      } catch {
        /* welcome panel still usable from lookup snapshot */
      }
    })();
  }, []);

  const goRegisterForMobile = useCallback(
    (e164, booking, newPerson = false) => {
      navigate(`${SALON_APP_BASE}/register`, {
        state: {
          stylistLed: true,
          // A family member booked on a number that already has another person's profile.
          newPerson: Boolean(newPerson),
          prefillMobile: e164,
          guestMobile: e164,
          guestName: booking?.customer_name || '',
          guestGender: booking?.customer_gender || '',
          engagementBooking: booking || engagementBooking || undefined,
          next: `${SALON_APP_BASE}/stylist-checkin`,
          registerMessage: newPerson
            ? `Register ${booking?.customer_name || 'the guest'} as a new guest on this number (gender is required for services).`
            : 'No profile for this number — register the guest (gender is required for services).',
        },
      });
    },
    [navigate, engagementBooking],
  );

  const lookupMobileForCheckin = useCallback(
    async (e164, booking) => {
      setBusy(true);
      setError('');
      setCandidates([]);
      try {
        const data = await lookupStylistCustomerByMobile(e164);
        if (data?.crm_imported) {
          setNotice('Loaded from salon CRM and saved to your guest list.');
        } else if (data?.crm?.merged_from_crm) {
          setNotice('Salon CRM details merged with this profile.');
        }
        if (booking) {
          setEngagementBooking(booking);
          setNotice((prev) =>
            prev
              ? prev
              : `Booking · ${booking.time_label || ''} ${booking.service_name || ''}`.trim(),
          );
          const found = data?.user || {};
          const foundName = found.name || found.full_name || '';
          if (booking.customer_name && foundName && !sameGuestName(booking.customer_name, foundName)) {
            // The number has another person's profile: pick it, or register the booked guest as new.
            setCandidates([{ id: found.id, name: foundName, full_name: foundName, mobile: found.mobile || e164, user: found }]);
            setStep('pick');
            setError(`This number has ${foundName}'s profile. The booking is for ${booking.customer_name}.`);
            return;
          }
        }
        openProfile(data.user, e164);
      } catch (err) {
        if (err?.code === 'AMBIGUOUS' && err.candidates?.length) {
          if (booking) setEngagementBooking(booking);
          const named = booking?.customer_name
            ? err.candidates.filter((c) => sameGuestName(booking.customer_name, c.name || c.full_name))
            : [];
          if (named.length === 1) {
            // Several people on this number: the booking names one of them.
            openProfile(
              { id: named[0].id, name: named[0].name || named[0].full_name, full_name: named[0].full_name || named[0].name, mobile: named[0].mobile },
              e164,
            );
            return;
          }
          setCandidates(err.candidates);
          setStep('pick');
          setError('Multiple profiles found. Select the correct customer.');
          return;
        }
        if (err?.code === 'NOT_FOUND') {
          goRegisterForMobile(e164, booking);
          return;
        }
        setError(err?.message || 'Lookup failed');
      } finally {
        setBusy(false);
      }
    },
    [goRegisterForMobile, openProfile],
  );

  const handleProfileSaved = useCallback(
    (data) => {
      const user = data?.user || profile?.user;
      const profilePayload = data?.profile || user?.profile || profile?.profile;
      if (!user?.id) return;
      setProfile({
        user: { ...user, profile: profilePayload },
        mobile: profile?.mobile,
        profile: profilePayload,
      });
      setNotice('Customer details updated.');
    },
    [profile],
  );

  useEffect(() => {
    setSalonKioskMode(KIOSK_MODE_DESK);
  }, []);

  useEffect(() => {
    if (location.state?.resumeProfile?.user?.id) return;
    const booking = location.state?.engagementBooking;
    if (booking && (booking.id || booking.appointment_id) && !bookingLookupStartedRef.current) {
      bookingLookupStartedRef.current = true;
      setEngagementBooking(booking);
      const phone = booking.customer_phone;
      if (!phone) {
        setError('This booking has no phone — enter mobile number below.');
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }
      setMobile(phone);
      void lookupMobileForCheckin(normalizeRegisterMobileValue(phone), booking);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, lookupMobileForCheckin, navigate]);

  useEffect(() => {
    const resume = location.state?.resumeProfile;
    const booking = location.state?.engagementBooking;
    if (booking) setEngagementBooking(booking);
    if (!resume?.user?.id) return;
    const phone =
      resume.mobile ||
      resume.user?.mobile ||
      location.state?.prefillMobile ||
      '';
    openProfile(resume.user, phone);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, openProfile]);

  const onLookup = async (event) => {
    event.preventDefault();
    const e164 = normalizeRegisterMobileValue(mobile);
    if (!isRegisterMobileComplete(e164)) {
      setError(mobileValidationError());
      return;
    }
    await lookupMobileForCheckin(e164, engagementBooking);
  };

  const pickCandidate = async (candidate) => {
    setBusy(true);
    setError('');
    try {
      const user = candidate.user || {
        id: candidate.id,
        name: candidate.name || candidate.full_name,
        full_name: candidate.full_name || candidate.name,
        mobile: candidate.mobile,
      };
      await openProfile(user, normalizeRegisterMobileValue(mobile) || candidate.mobile);
    } catch (err) {
      setError(err?.message || 'Could not load profile');
    } finally {
      setBusy(false);
    }
  };

  const resumeActiveVisit = () => {
    if (!activeVisit?.id) return;
    setActiveCheckinSession({
      id: activeVisit.id,
      code: activeVisit.customer_code,
      status: activeVisit.status,
      faceUserId: profile?.user?.id,
    });
    handoffFromCheckin(activeVisit, {
      faceUserId: profile?.user?.id,
      customerName: displayName,
      mobile: profile?.mobile,
    });
    navigate(SALON_STYLIST_GUEST_PATH, {
      state: { checkinId: activeVisit.id, stylistLed: true, resumed: true },
    });
  };

  const ensureVisit = useCallback(
    async () => {
      if (!profile?.user?.id) throw new Error('Customer not loaded');
      if (!stylistGuestGenderReady(profile.user, profile.profile)) {
        throw new Error('GENDER_REQUIRED');
      }
      const existing = await fetchActiveVisit({ faceUserId: profile.user.id });
      if (existing?.id) {
        setActiveVisit(existing);
        throw new Error('ACTIVE_VISIT');
      }
      const now = new Date().toISOString();
      const name = displayName;
      const phone = profile.mobile;
      if (engagementBooking) {
        const apptId = engagementBooking.appointment_id || engagementBooking.id;
        const data = await checkinFromEngagementBooking(apptId, {
          faceUserId: profile.user.id,
        });
        const checkin = data?.checkin;
        if (!checkin?.id) throw new Error('Could not open visit from booking');
        setActiveCheckinSession({
          id: checkin.id,
          code: checkin.customer_code,
          status: checkin.status,
          faceUserId: profile.user.id,
        });
        handoffFromCheckin(checkin, {
          faceUserId: profile.user.id,
          customerName: name,
          mobile: phone,
        });
        return checkin;
      }
      let checkin = await createPartnerCheckinQueue({
        faceUserId: profile.user.id,
        customerName: name,
        mobile: phone,
        selectedServices: [],
        startService: false,
        source: 'stylist_mobile',
        customerSnapshot: {
          name,
          full_name: name,
          mobile: phone,
          face_user_id: profile.user.id,
          guest_confirmed_at: now,
          front_desk_confirmed_at: now,
          entry_source: 'stylist_mobile',
          stylist_led: true,
          billing_state: 'open',
          payment_state: 'unpaid',
        },
      });
      if (!checkin?.id) throw new Error('Could not open visit');
      if (checkin.reused_open_visit) {
        setNotice('Resumed existing open visit for this customer.');
      }
      setActiveCheckinSession({
        id: checkin.id,
        code: checkin.customer_code,
        status: checkin.status,
        faceUserId: profile.user.id,
      });
      handoffFromCheckin(checkin, { faceUserId: profile.user.id, customerName: name, mobile: phone });
      return checkin;
    },
    [displayName, engagementBooking, profile],
  );

  const continueToStylistPick = async () => {
    if (!profile) return;
    if (activeVisit?.id) {
      setError('This customer already has an open visit. Resume it or ask front desk to cancel.');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const checkin = await ensureVisit();
      navigate(SALON_STYLIST_GUEST_PATH, {
        state: { checkinId: checkin.id, stylistLed: true },
      });
    } catch (err) {
      if (err?.message === 'ACTIVE_VISIT') {
        setError('Open visit exists — use Resume visit below.');
      } else if (err?.message === 'GENDER_REQUIRED') {
        setError('Add gender under Customer info (Edit), then continue.');
      } else {
        setError(err?.message || 'Could not start visit');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="salon-code-page page-bg fi-lux-theme fi-scroll-page">
      <div className="salon-code-page__stage">
        <header className="salon-code-page__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
            imgClassName="salon-code-page__logo"
            fallbackClassName="salon-code-page__logo-text"
          />
          <FaceIdentityFlowNav
            onBack={() => {
              if (step === 'profile' || step === 'pick') setStep('lookup');
              else navigate(SALON_SERVICE_LOBBY_PATH, { replace: false });
            }}
            homePath={SALON_SERVICE_LOBBY_PATH}
          />
        </header>

        {step === 'lookup' ? (
          <form className="salon-code-page__form" onSubmit={onLookup}>
            <ExpectedTodayBookings compact />
            <p className="salon-code-page__eyebrow">Stylist check-in</p>
            <h1 className="salon-code-page__title">Customer mobile number</h1>
            <p className="salon-code-page__lede">
              Search this outlet&apos;s guests. No Passport or guest kiosk required.
            </p>
            <label className="salon-code-page__label" htmlFor="stylist-mobile-lookup">
              Mobile number
            </label>
            <div className="salon-code-page__phone fi-register__phone">
              <CountryWhatsAppInput
                id="stylist-mobile-lookup"
                value={mobile}
                onChange={setMobile}
                defaultCountry={DEFAULT_WHATSAPP_COUNTRY}
                disabled={busy}
              />
            </div>
            {error ? <p className="salon-code-page__error" role="alert">{error}</p> : null}
            <button type="submit" className="salon-code-page__submit" disabled={busy}>
              {busy ? 'Searching…' : 'Search customer'}
            </button>
          </form>
        ) : null}

        {step === 'pick' ? (
          <div className="salon-visit-intent__content" style={{ paddingTop: 0 }}>
            <h1 className="salon-visit-intent__title">Select customer</h1>
            <p className="salon-visit-intent__lede">{error || 'Multiple profiles match this number.'}</p>
            <ul className="salon-service-lobby__list">
              {candidates.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="salon-visit-intent__card"
                    style={{ width: '100%', marginBottom: '0.5rem' }}
                    disabled={busy}
                    onClick={() => pickCandidate(c)}
                  >
                    <span className="salon-visit-intent__card-title">
                      {c.name || c.full_name || 'Guest'}
                    </span>
                    <span className="salon-visit-intent__card-hint">{maskMobile(c.mobile)}</span>
                  </button>
                </li>
              ))}
            </ul>
            {engagementBooking?.customer_name ? (
              <button
                type="button"
                className="salon-visit-intent__card"
                style={{ width: '100%' }}
                disabled={busy}
                onClick={() =>
                  goRegisterForMobile(
                    normalizeRegisterMobileValue(mobile || engagementBooking.customer_phone),
                    engagementBooking,
                    true,
                  )
                }
              >
                <span className="salon-visit-intent__card-title">
                  Register {engagementBooking.customer_name} as a new guest
                </span>
                <span className="salon-visit-intent__card-hint">Same mobile number, separate profile and history</span>
              </button>
            ) : null}
          </div>
        ) : null}

        {step === 'profile' && profile?.user ? (
          <div className="fi-welcome fi-welcome--passport fi-welcome--guest stylist-checkin-welcome">
            <GuestWelcomePassportPanel
              user={profile.user}
              profile={profile.profile}
              statusBanner={
                <>
                  {activeVisit?.id ? (
                    <p className="salon-service-session__banner" role="status">
                      Open visit · code {activeVisit.customer_code || activeVisit.id}
                      <button
                        type="button"
                        className="salon-service-session__link"
                        style={{ marginLeft: '0.5rem' }}
                        onClick={resumeActiveVisit}
                      >
                        Resume visit →
                      </button>
                    </p>
                  ) : null}
                  {notice ? (
                    <p className="salon-service-session__banner" role="status">{notice}</p>
                  ) : null}
                </>
              }
            >
              <WelcomePreferencesCard
                registerForm={registerForm}
                preferences={profile.profile?.preferences || profile.profile}
                user={profile.user}
                membership={profile.profile?.membership || profile.user?.membership}
                faceUserId={profile.user.id}
                onSaved={handleProfileSaved}
                heading="Customer info"
              />
              <div className="salon-visit-intent__content stylist-checkin-welcome__actions" style={{ paddingTop: 0 }}>
                <p className="salon-visit-intent__lede">
                  Next: choose your stylist, then service list or skin &amp; hair analysis.
                </p>
                {!genderReady ? (
                  <p className="salon-service-session__banner" role="status">
                    Gender is required — tap Edit on Customer info.
                    {engagementBooking?.customer_gender
                      ? ` The booking says ${engagementBooking.customer_gender === 'female' ? 'Female' : 'Male'}.`
                      : ''}
                  </p>
                ) : null}
                {error ? <p className="salon-service-session__error">{error}</p> : null}
                <button
                  type="button"
                  className="salon-service-session__primary salon-service-session__primary--wide"
                  disabled={busy || Boolean(activeVisit?.id) || !genderReady}
                  onClick={() => continueToStylistPick()}
                >
                  {busy ? 'Starting…' : engagementBooking ? 'Check in & continue' : 'Continue'}
                </button>
              </div>
            </GuestWelcomePassportPanel>
          </div>
        ) : null}
      </div>
    </div>
  );
}
